import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { format } from 'date-fns'

import MarkDown from '~/components/Helpers/MarkDown'
import PrimaryCTA from '~/components/UI/Buttons/PrimaryCTA/PrimaryCTA'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'
import Pin3 from '~/assets/icons/pin-3'

const cardVariants = cva(
  'flex max-h-[500px] !h-[500px] flex-col p-xs-plus sm:basis-[calc(50%-12px)] sm:p-s lg:grow lg:basis-0 lg:p-m',
  {
    variants: {
      backgroundColour: {
        'ink-blue': 'bg-blue-d text-white',
        'mid-blue': 'bg-blue-m text-white',
        'light-blue': 'bg-blue-l text-blue-d',
        chartreuse: 'bg-char text-blue-d',
        'light-pink': 'bg-pink text-blue-d',
        'light-aqua': 'bg-green text-blue-d',
        'light-purple': 'bg-purple text-blue-d',
        'light-orange': 'bg-orange text-blue-d',
        'light-grey': 'bg-grey-l text-blue-d',
        white: 'bg-white text-blue-d'
      }
    },
    defaultVariants: {
      backgroundColour: 'ink-blue'
    }
  }
)

export type EventCardProps = CardProps & {
  content: {
    date: string
    switchHeading?: boolean
    showIcon?: boolean
    ctaButtons?: boolean
  }
}

const EventCard = ({ content, carousel, className }: EventCardProps) => {
  const {
    backgroundColour,
    heading,
    date,
    switchHeading,
    showIcon,
    copy,
    buttons,
    ctaButtons
  } = content

  const button = buttons ? buttons[0] : null
  const formattedDate = format(date, 'eeee dd MMMM yyyy')

  const foregroundColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  const iconColour =
    backgroundColour === 'ink-blue' || backgroundColour === 'mid-blue'
      ? '#82DAF4'
      : button?.strongColour
        ? '#1D4CF2'
        : '#0E1647'

  return (
    <div className={cn(cardVariants({ backgroundColour }), className)}>
      <div className='gap-xs flex grow flex-col justify-center'>
        <div className='tag'>{switchHeading ? heading : formattedDate}</div>
        <h2
          className={cn(
            'font-serif',
            carousel ? 'text-h2-lg' : 'text-h1-reg-md'
          )}
        >
          {switchHeading ? formattedDate : heading}
        </h2>
        <div className={cn('gap-xs flex', carousel ? 'mt-2xs' : 'mt-xs')}>
          {showIcon && (
            <Pin3
              fill={foregroundColour}
              secondaryfill={iconColour}
              className='h-xs-plus w-auto self-center'
            />
          )}
          <MarkDown content={copy} />
        </div>
      </div>
      {button && (
        <div className='mt-s w-fit'>
          {ctaButtons ? (
            <PrimaryCTA data={button} colour={foregroundColour} />
          ) : (
            <TertiaryLink data={button} colour={foregroundColour} />
          )}
        </div>
      )}
    </div>
  )
}

export default EventCard
